<template>
    <div>
        <!-- Drawing attachment -->
        <div class="row mb-4"
             v-if="marking.type === 'draw' && marking.drawing && marking.drawing.attachment">
            <div class="col-sm-12">
                <img class="img-fluid" :src="marking.drawing.attachment" alt="attachment"/>
                <p v-if="marking.drawing.description"
                   class="mb-0 mt-2 text-pre-wrap"
                   v-text="marking.drawing.description"></p>
            </div>
        </div>

        <!-- Attachments -->
        <div class="row">
            <div v-for="(attachment, g) in activeAttachments"
                 :key="g"
                 class="mb-4 d-flex justify-content-center"
                 :class="cardClass">
                <div class="card shadow-sm rounded bg-white position-relative attachment-card">
                    <!-- Remove button -->
                    <b-button
                        class="position-absolute rounded-circle p-0 attachment-circle"
                        v-if="showControls"
                        @click="$emit('remove-image', marking.attachments.indexOf(attachment))"
                        v-b-tooltip.hover
                        title="Remove">
                        <b-icon-trash class="text-white"></b-icon-trash>
                    </b-button>

                    <!-- Image -->
                    <div class="image-wrapper" @click="$emit('open-image', attachment)">
                        <img
                            class="img-fluid rounded w-100 h-100"
                            :src="attachment.attachment ? getAttachmentURL(attachment) : attachment.url"
                            alt="attachment"
                            style="object-fit: cover;"
                        />
                        <div class="image-overlay">
                            <b-icon-zoom-in class="overlay-icon"></b-icon-zoom-in>
                        </div>
                    </div>

                    <!-- Description -->
                    <p v-if="attachment.description || attachment.is_before || attachment.is_after"
                       class="mb-0 text-muted text-center position-absolute w-100 attachment-description"
                    >{{ attachmentLabel(attachment) }}</p>
                </div>
            </div>

            <!-- Upload button -->
            <div v-if="!hasHitAttachmentLimits && showControls"
                 class="mb-4 max-h text-center"
                 :class="cardClass">
                <div class="card shadow-sm rounded-4 p-1 upload-card"
                     @click="$refs.pictureInput.click()">
                    <div class="upload-placeholder">
                        <b-icon-card-image class="mb-2 h-50 w-50"/>
                        <div>Upload picture</div>
                    </div>
                    <input
                        ref="pictureInput"
                        accept="image/*"
                        type="file"
                        style="display: none;"
                        @change="handleUpload"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "AttachmentsViewer",
    props: {
        showControls: {
            type: Boolean,
            default: true,
        },
        showLogDetails: {
            type: Boolean,
            default: true,
        },
        marking: {
            type: Object,
            required: true
        },
        cardClass: {
            type: String,
            default: "col-12 col-sm-4"
        },
        hasHitAttachmentLimits: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        activeAttachments() {
            return this.marking.attachments.filter(a => !a.is_trashed);
        }
    },
    methods: {
        attachmentLabel(attachment) {
            const prefix = attachment.is_before ? 'Image Before. '
                         : attachment.is_after  ? 'Image After. '
                         : '';
            return prefix + (attachment.description || '');
        },
        getAttachmentURL(attachment) {
            if (typeof attachment.attachment === 'string') {
                return attachment.attachment;
            } else {
                return fileUtils.blobToImgSrc(attachment.attachment);
            }
        },
        handleUpload(event) {
            const file = event.target.files[0];
            if (!file) return;
            this.$emit('upload', file);
            // Reset input so same file can be uploaded again
            event.target.value = null;
        }
    }
}
</script>

<style scoped>
.marking-modal-scroll-wrapper {
    min-height: 400px;
    height: 60vh;
}

.card {
    box-shadow: 0 .125rem .25rem rgba(0,0,0,.075);
    border-radius: 0.25rem;
    background-color: #fff;
}

.attachment-card {
    position: relative;
    width: 100%;
    max-width: 300px;
}

.attachment-circle {
    position: absolute;
    top: 5px;
    right: 5px;
    width: 30px;
    height: 30px;
    padding: 0.25rem;
}

.image-wrapper {
    position: relative;
    width: 100%;
    padding-top: 100%; /* Maintain square aspect ratio */
    overflow: hidden;
    cursor: pointer;
}

.image-wrapper img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.image-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    opacity: 0;
    transition: opacity 0.2s;
}

.image-wrapper:hover .image-overlay {
    opacity: 1;
}

.attachment-description {
    bottom: 0;
    left: 0;
    padding: 0.25rem;
    background: rgba(255, 255, 255, 0.7);
    position: absolute;
    width: 100%;
    text-align: center;
}

.upload-card {
    cursor: pointer;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 1rem;
    height: 100%;
}

.upload-placeholder {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
}
</style>
